export type PredictionRow = Record<string, string>;

export type Distribution = Record<string, number>;

export interface RawPrediction {
  winner: { home: number; away: number; draw?: number };
  total_goals: Distribution;
  home_goals: Distribution;
  away_goals: Distribution;
  classifiers?: Record<'total' | 'home' | 'away', Record<string, Distribution>>;
}

export interface Bucket {
  label: string;
  value: number;
}

export interface TotalLine {
  line: string;
  over: number;
  under: number;
}

export interface Combo {
  side: 'home' | 'away';
  kind: 'o' | 'u';
  line: string;
  value: number;
}

export interface ClassifierRow {
  name: string;
  total: number;
  home: number;
  away: number;
}

export interface MatchAnalysis {
  home: string;
  away: string;
  date: string;
  threeWay: boolean;
  winner: { home: number; away: number };
  regulation: { home: number; draw: number; away: number };
  doubleChance: { home: number; away: number; either: number };
  btts: { yes: number; over25: number };
  winToNil: { home: number; away: number };
  correctScores: Bucket[];
  homeGoals: Bucket[];
  awayGoals: Bucket[];
  totalGoals: Bucket[];
  totals: TotalLine[];
  handicap: { home: number; away: number };
  combos: Combo[];
  expected: { home: number; away: number; total: number } | null;
  score: { home: number; away: number; probability: number } | null;
  classifiers: { rows: ClassifierRow[]; ensemble: ClassifierRow } | null;
}

const num = (row: PredictionRow, key: string): number => {
  const v = parseFloat(row[key]);
  return Number.isFinite(v) ? v : 0;
};

const linesFor = (row: PredictionRow, pattern: RegExp): number[] =>
  Object.keys(row)
    .map((k) => k.match(pattern)?.[1])
    .filter((v): v is string => Boolean(v))
    .map(Number)
    .sort((a, b) => a - b);

const entries = (dist: Distribution): [number, number][] =>
  Object.entries(dist)
    .map(([k, v]) => [Number(k), v] as [number, number])
    .sort((a, b) => a[0] - b[0]);

const mean = (dist: Distribution): number => entries(dist).reduce((s, [k, p]) => s + k * p, 0);

const rawBuckets = (dist: Distribution, tailFrom = Infinity): Bucket[] => {
  const sorted = entries(dist);
  const last = Math.min(tailFrom, sorted[sorted.length - 1]?.[0] ?? 0);
  const buckets: Bucket[] = sorted
    .filter(([k]) => k < last)
    .map(([k, p]) => ({ label: String(k), value: p }));
  const tail = sorted.filter(([k]) => k >= last).reduce((sum, [, p]) => sum + p, 0);
  if (sorted.length) buckets.push({ label: `${last}+`, value: tail });
  return buckets;
};

const likeliestScore = (raw: RawPrediction, homeFavourite: boolean, allowDraw: boolean) => {
  let best = { home: 0, away: 0, probability: -1 };
  entries(raw.home_goals).forEach(([h, ph]) => {
    entries(raw.away_goals).forEach(([a, pa]) => {
      const consistent = allowDraw || (homeFavourite ? h > a : a > h);
      const p = ph * pa;
      if (consistent && p > best.probability) best = { home: h, away: a, probability: p };
    });
  });
  return best;
};

const CLASSIFIER_NAMES: Record<string, string> = {
  svm: 'SVM',
  knn: 'KNN',
  dt: 'Decision tree',
  gbdt: 'Gradient boosting',
  rf: 'Random forest',
  et: 'Extra trees',
  absvm: 'AdaBoost',
  lr: 'Logistic regression',
};

const classifierRows = (raw: RawPrediction): MatchAnalysis['classifiers'] => {
  const c = raw.classifiers;
  if (!c?.total) return null;
  const rows = Object.keys(c.total).map((key) => ({
    name: CLASSIFIER_NAMES[key] ?? key,
    total: mean(c.total[key]),
    home: c.home?.[key] ? mean(c.home[key]) : 0,
    away: c.away?.[key] ? mean(c.away[key]) : 0,
  }));
  return {
    rows,
    ensemble: {
      name: 'Ensemble',
      total: mean(raw.total_goals),
      home: mean(raw.home_goals),
      away: mean(raw.away_goals),
    },
  };
};

export const analyzeMatch = (row: PredictionRow, raw?: RawPrediction | null): MatchAnalysis => {
  const threeWay = raw?.winner.draw !== undefined || !('1ML' in row);
  const winner = threeWay
    ? { home: num(row, '1'), away: num(row, '2') }
    : { home: num(row, '1ML'), away: num(row, '2ML') };
  const homeFavourite = winner.home >= winner.away;

  const homeGoals = raw ? rawBuckets(raw.home_goals) : [];
  const awayGoals = raw ? rawBuckets(raw.away_goals) : [];
  const totalGoals = raw ? rawBuckets(raw.total_goals, 9) : [];
  const expected = raw
    ? { home: mean(raw.home_goals), away: mean(raw.away_goals), total: mean(raw.total_goals) }
    : null;
  const score = raw ? likeliestScore(raw, homeFavourite, threeWay) : null;
  const correctScores = Object.keys(row)
    .filter((k) => /^\d+:\d+$/.test(k))
    .map((k) => ({ label: k, value: num(row, k) }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 4);

  const combos: Combo[] = Object.keys(row)
    .map((k) => k.match(/^(home|away)\/([ou])(\d+\.5)$/))
    .filter((m): m is RegExpMatchArray => Boolean(m))
    .map((m) => ({
      side: m[1] as Combo['side'],
      kind: m[2] as Combo['kind'],
      line: m[3],
      value: num(row, m[0]),
    }));

  return {
    home: row.home,
    away: row.away,
    date: row.date,
    threeWay,
    winner,
    regulation: { home: num(row, '1'), draw: num(row, 'X'), away: num(row, '2') },
    doubleChance: { home: num(row, '1X'), away: num(row, '2X'), either: num(row, '12') },
    btts: { yes: num(row, 'BTTS'), over25: num(row, 'BTTS/o2.5') },
    winToNil: { home: num(row, 'home_win_to_nil'), away: num(row, 'away_win_to_nil') },
    correctScores,
    homeGoals,
    awayGoals,
    totalGoals,
    totals: linesFor(row, /^over(\d+\.5)$/).map((l) => ({
      line: String(l),
      over: num(row, `over${l}`),
      under: num(row, `under${l}`),
    })),
    handicap: { home: num(row, 'home_-1.5'), away: num(row, 'away_-1.5') },
    combos,
    expected,
    score,
    classifiers: raw ? classifierRows(raw) : null,
  };
};

export const pct = (v: number, digits = 0) => `${(v * 100).toFixed(digits)}%`;
